import { api } from '@shared/api';

/*
 * Calendars (apps/api/tibyan/calendars/router.py, prefix /calendar). Hijri routes work without an org.
 * Mutations marked [CA] may answer 409 HOLIDAY_AFFECTS_BOOKINGS {affected_occurrences, affected_students,
 * occurrence_ids}; repeat with ?confirm_affected=true after the admin confirms.
 */

export type L10n = { ar?: string; en?: string; fr?: string; fa?: string };
export type Authority = 'khamenei_office' | 'umm_al_qura' | 'custom';
export type HolidayKind = 'holiday' | 'eid' | 'closure';
export type AppliesTo = 'all_sessions' | 'regular_only';

export interface Cascade {
  holidays_created: number;
  holidays_redated: number;
  holidays_confirmed: number;
  occurrences_cancelled: number;
  occurrences_reflagged: number;
  organizations?: number;
}

export interface HijriDay {
  gregorian: string;
  hijri: { year: number; month: number; day: number; month_name: L10n; iso: string } | null;
  status: 'confirmed' | 'tentative' | null;
  tentative?: boolean;
  source: 'authority' | 'projection' | null;
  authority?: string;
  month_length?: 29 | 30 | null;
}

export interface MonthStart {
  id: string;
  authority: Authority;
  organization_id: string | null;
  hijri_year: number;
  hijri_month: number;
  month_name: L10n;
  gregorian_start_date: string;
  previous_gregorian_start: string | null;
  status: 'tentative' | 'confirmed';
  source: 'seed_table' | 'official_announcement' | 'manual';
  confirmed_at: string | null;
  confirmed_by: string | null;
  announced_at: string | null;
  note: string | null;
}

export interface MonthStartIn {
  authority: Authority;
  hijri_year: number;
  hijri_month: number;
  gregorian_start_date: string;
  source?: MonthStart['source'];
  note?: string;
}

export interface ConfirmMonthIn {
  authority: Authority;
  /** At most ±1 day from the current start. */
  gregorian_start_date?: string;
  source?: MonthStart['source'];
  announced_at?: string;
  note?: string;
}

export interface Holiday {
  id: string;
  name: L10n;
  starts_on: string;
  ends_on: string;
  kind: HolidayKind | 'ramadan';
  tentative: boolean;
  source: 'manual' | 'hijri_projection';
  resolution: 'authority' | 'projection' | null;
  applies_to: AppliesTo;
  is_enabled: boolean;
  hijri_year: number | null;
  rule_id: string | null;
  seed_key?: string | null;
  confirmed_at: string | null;
  day_off: boolean;
}

export interface HolidayIn {
  name: L10n;
  starts_on: string;
  ends_on: string;
  kind?: HolidayKind;
  applies_to?: AppliesTo;
  tentative?: boolean;
}

export interface SeedItem {
  key: string;
  hijri: { month: number; day: number | null; last_day: boolean; length_days: number | null };
  kind: 'holiday' | 'eid' | 'ramadan';
  optional: boolean;
  name: { ar: string; en: string; fr: string; fa?: string };
}

export interface HolidayRule {
  id: string;
  seed_key: string | null;
  seed_set: string | null;
  name: L10n;
  hijri: { month: number; day: number | null; last_day: boolean; length_days: number | null };
  kind: string;
  applies_to: AppliesTo;
  is_enabled: boolean;
  is_optional: boolean;
}

export interface RamadanPeriod {
  hijri_year: number;
  starts_on: string;
  ends_on: string;
  start_status: 'confirmed' | 'tentative';
  end_status: 'confirmed' | 'tentative';
  status: string;
  source: 'authority' | 'projection';
}

export interface RamadanResponse {
  authority: Authority;
  authority_label: L10n;
  enabled: boolean;
  shift_rule: string;
  prayer_calc_method: string;
  prayer_calc_method_label: L10n;
  location_configured: boolean;
  data: RamadanPeriod[];
}

export interface CalendarSettings {
  hijri_authority: Authority;
  hijri_authority_label: L10n;
  prayer_calc_method: string;
  holiday_set: 'shia' | 'custom';
  holidays_confirm_manually: boolean;
  version: number;
  [key: string]: unknown;
}

export type RescheduleStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'expired';

export interface RescheduleRequest {
  id: string;
  occurrence_id: string;
  status: RescheduleStatus;
  kind: 'reschedule';
  requested_by_user_id: string;
  requested_by_role: string;
  counterpart: 'teacher' | 'student';
  proposed_start: string;
  proposed_end: string;
  reason: string;
  is_within_late_window: boolean;
  expires_at: string;
  decided_by_user_id: string | null;
  decided_at: string | null;
  decision_note: string | null;
  new_occurrence_id: string | null;
  created_at: string;
}

export interface RescheduleIn {
  proposed_start: string;
  proposed_end?: string;
  reason: string;
  accept_late_consequences?: boolean;
}

const q = (params: Record<string, string | number | boolean | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};
const ca = (confirm?: boolean) => (confirm ? '?confirm_affected=true' : '');

export const calendarApi = {
  settings: () => api.get<CalendarSettings>('/calendar/settings'),
  hijri: (from: string, to: string) => api.get<{ authority: Authority; authority_label: L10n; data: HijriDay[] }>(`/calendar/hijri${q({ from, to })}`),
  monthStarts: (params: { authority?: Authority; hijri_year?: number } = {}) => api.get<{ data: MonthStart[] }>(`/calendar/hijri/month-starts${q(params)}`),
  addMonthStart: (body: MonthStartIn) => api.post<MonthStart & { cascade?: Cascade }>('/calendar/hijri/month-starts', body),
  confirmMonth: (y: number, m: number, body: ConfirmMonthIn) => api.post<MonthStart & { cascade?: Cascade }>(`/calendar/hijri/month-starts/${y}/${m}/confirm`, body),
  holidays: (from?: string, to?: string) => api.get<{ data: Holiday[] }>(`/calendar/holidays${q({ from, to })}`),
  addHoliday: (body: HolidayIn, confirm?: boolean) => api.post<Holiday>(`/calendar/holidays${ca(confirm)}`, body),
  patchHoliday: (id: string, body: Partial<HolidayIn> & { is_enabled?: boolean }, confirm?: boolean) =>
    api.patch<Holiday>(`/calendar/holidays/${encodeURIComponent(id)}${ca(confirm)}`, body),
  confirmHoliday: (id: string, confirm?: boolean) => api.post<Holiday>(`/calendar/holidays/${encodeURIComponent(id)}/confirm${ca(confirm)}`),
  deleteHoliday: (id: string) => api.delete<void>(`/calendar/holidays/${encodeURIComponent(id)}`),
  rules: () => api.get<{ data: HolidayRule[] }>('/calendar/holiday-rules'),
  seeds: () => api.get<{ data: { shia: SeedItem[] } }>('/calendar/holiday-seeds'),
  applySeeds: (body: { set?: 'shia'; ticked?: string[]; unticked?: string[] }, confirm?: boolean) =>
    api.post<{ data: HolidayRule[]; cascade: Cascade }>(`/calendar/holiday-seeds/apply${ca(confirm)}`, body),
  ramadan: () => api.get<RamadanResponse>('/calendar/ramadan'),
  requestReschedule: (occurrenceId: string, body: RescheduleIn) =>
    api.post<RescheduleRequest>(`/calendar/occurrences/${encodeURIComponent(occurrenceId)}/reschedule-requests`, body),
  rescheduleRequests: (status?: RescheduleStatus) => api.get<{ data: RescheduleRequest[] }>(`/calendar/reschedule-requests${q({ status })}`),
  approve: (id: string, body: { note?: string; excused?: boolean } = {}) => api.post<RescheduleRequest>(`/calendar/reschedule-requests/${encodeURIComponent(id)}/approve`, body),
  decline: (id: string, note?: string) => api.post<RescheduleRequest>(`/calendar/reschedule-requests/${encodeURIComponent(id)}/decline`, note ? { note } : {}),
  withdraw: (id: string) => api.post<RescheduleRequest>(`/calendar/reschedule-requests/${encodeURIComponent(id)}/withdraw`),
};

export const calendarKeys = {
  all: ['calendar'] as const,
  hijri: (from: string, to: string) => ['calendar', 'hijri', from, to] as const,
  monthStarts: (orgId: string | null, year: number | null) => ['calendar', 'month-starts', orgId, year] as const,
  holidays: (orgId: string | null) => ['calendar', 'holidays', orgId] as const,
  seeds: (orgId: string | null) => ['calendar', 'seeds', orgId] as const,
  rules: (orgId: string | null) => ['calendar', 'rules', orgId] as const,
  ramadan: (orgId: string | null) => ['calendar', 'ramadan', orgId] as const,
  settings: (orgId: string | null) => ['calendar', 'settings', orgId] as const,
  reschedule: (orgId: string | null, status: string) => ['calendar', 'reschedule', orgId, status] as const,
};

/** Localized API label: requested language → English → any. */
export function l10n(v: L10n | null | undefined, locale: string): string {
  if (!v) return '';
  const r = v as Record<string, string | undefined>;
  return r[locale] || r.en || Object.values(r).find(Boolean) || '';
}
